import { Box } from '@mui/material';
import React, {
  useImperativeHandle,
  useRef,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import FormHandler from './FormHandler';
import FieldGenerator from './FieldGenerator';
import getFieldPermission from '../../helpers/getFieldPermission';
import getYupLeafFieldPaths from '../../helpers/getYupFieldPaths';
import getNamesFromSchema from '../../helpers/getNamesFromSchema';
import getValueByDotNotation from '../../helpers/getValueByDotNotation';
import LoadingEvent from '../UI/Skeleton/LoadingEvent';
import { useUserAccess } from '../../providers/UserAccessProvider';
import { useEntityPermission } from '../../providers/EntityPermissionProvider';

/**
 * Return fields that are in array1 but not in array2.
 *
 * @param {string[]} array1 - All fields from form schema
 * @param {string[]} array2 - Fields already in validation schema
 * @returns {string[]} - Missing fields
 */
function getMissingFields(array1, array2) {
  const validationSet = new Set(array2);
  return array1.filter(f => !validationSet.has(f));
}

const addFormSchemaEssentials = ({
  schema,
  permissions,
  userAccess,
  initialValues,
}) => {
  if (!Array.isArray(schema)) return schema;
  const newSchema = [];
  schema.forEach(field => {
    const newField = { ...field };

    newField.initialValue = getValueByDotNotation(initialValues, field?.name);

    const derivedPermission = getFieldPermission({
      field: newField,
      permissions,
    });

    if (derivedPermission === 'NO_PERMISSION') {
      return;
    }

    newField.permission = derivedPermission;

    if (Array.isArray(field.children)) {
      newField.children = addFormSchemaEssentials({
        schema: field.children,
        permissions,
        userAccess,
      });
    }

    if (newField?.children) {
      if (Array.isArray(newField.children) && newField.children.length > 0) {
        newSchema.push(newField);
      }
    } else {
      newSchema.push(newField);
    }
  });

  return newSchema;
};

const FormEdit = forwardRef(
  (
    {
      errors = null,
      warnings = null,
      initialValues,
      schema,
      children,
      onSubmit,
      customComponents = {},
      validationSchema = null,
      sendDirtyOnly = false,
      permissionEntity = null,
    },
    ref,
  ) => {
    const formRef = useRef();
    const userAccess = useUserAccess();
    const { getPermission } = useEntityPermission();
    const [processedSchema, setProcessedSchema] = useState(schema);
    const [loading, setLoading] = useState(true);
    const [validationSchemaFinal, setValidationSchemaFinal] =
      useState(validationSchema);

    const [permissions, setPermissions] = useState(null);

    useImperativeHandle(ref, () => ({
      submit: () => {
        if (formRef.current) {
          formRef.current.submit();
        }
      },
      reset: args => {
        if (formRef.current) {
          formRef.current.reset(args);
        }
      },
      resetAndNavigate: url => {
        if (formRef.current) {
          formRef.current.resetAndNavigate(url);
        }
      },
      getFormState: () => {
        if (formRef.current) {
          return formRef.current.getFormState();
        }
        return null;
      },
      isFormDirty: () => {
        if (formRef.current) {
          return formRef.current.isFormDirty();
        }
        return false;
      },
      getValues: () => {
        if (formRef.current) {
          return formRef.current.getValues();
        }
        return false;
      },
    }));

    const initialize = async () => {
      setLoading(true);
      if (permissionEntity) {
        const permissionsFound = await getPermission({
          name: permissionEntity,
        });

        setPermissions(permissionsFound);

        const newSchema = await addFormSchemaEssentials({
          schema,
          permissions: permissionsFound,
          userAccess,
          initialValues,
        });

        const removeFieldsFromValidation = getMissingFields(
          getYupLeafFieldPaths(validationSchema),
          getNamesFromSchema(newSchema),
        );

        let validationSchemaTemp = validationSchema;
        removeFieldsFromValidation.forEach(v => {
          validationSchemaTemp = validationSchemaTemp.omit(v);
        });

        setValidationSchemaFinal(validationSchemaTemp);

        setProcessedSchema(newSchema);
      }

      setLoading(false);
    };

    useEffect(() => {
      initialize();
    }, []);

    if (loading) {
      return <LoadingEvent>...Processing Schema</LoadingEvent>;
    }

    return (
      <Box>
        <FormHandler
          formSchema={processedSchema}
          sendDirtyOnly={sendDirtyOnly}
          errors={errors}
          warnings={warnings}
          ref={formRef}
          validationSchema={validationSchemaFinal}
          initialValues={initialValues}
          onSubmit={onSubmit}>
          <div className="form-fields">
            <FieldGenerator
              customComponents={customComponents}
              schema={processedSchema}
              permissions={permissions}
            />
            {children}
          </div>
        </FormHandler>
      </Box>
    );
  },
);

export default observer(FormEdit);
